import Duck from 'extensible-duck';
import _set from 'lodash/set';
import { geoOverlayType } from 'constants/geo-constants';
import { addressDetailFormField } from 'components/v2/anytime-booking/overlay/address-detail/form-validator';
import _cloneDeep from 'lodash/cloneDeep';

export default new Duck({
  namespace: 'GEWEB',
  store: 'GEO',
  initialState: {
    userLocation: undefined,
    suggestPOIs: {
      pickup: {
        fetching: false,
        pois: undefined,
      },
      dropoff: {
        fetching: false,
        pois: undefined,
      },
    },
    savedPOI: {
      predictPOIs: undefined,
      favoritePOIs: undefined,
      addressDetailOverlay: {
        addressDetailOverlayVisible: false,
        overlayType: geoOverlayType.addressDetail,
      },
      currentEditingPOI: {
        originalPOI: undefined,
        currentPOI: undefined,
      },
    },
  },
  types: [
    'RECEIVE_USER_LOCATION',
    'MAP_SEARCH',
    'FETCH_DEFAULT_PICKUP_STEP',
    'FETCH_SUGGEST_POIS',
    'SET_SUGGEST_POIS',
    'FETCH_SAVED_POIS',
    'SET_FAVORITE_POIS',
    'SET_PREDICT_POIS',
    // R2 Saved POI epic
    'OPEN_ADDRESS_DETAIL_OVERLAY',
    'CLOSE_ADDRESS_DETAIL_OVERLAY',
    'SET_CURRENT_EDITING_POI',
    'UPDATE_CURRENT_EDITING_POI_INFO',
    'SAVE_FAVORITE_POI',
    'DELETE_FAVORITE_POI',
  ],
  creators: duck => ({
    receiveUserLocation: (latitude, longitude) => ({
      type: duck.types.RECEIVE_USER_LOCATION,
      latitude,
      longitude,
    }),
    mapSearch: (latitude, longitude, pickupOrDropoff, callback) => ({
      type: duck.types.MAP_SEARCH,
      latitude,
      longitude,
      pickupOrDropoff,
      callback,
    }),
    fetchDefaultPickUpStepForDelivery: deliveryID => ({
      type: duck.types.FETCH_DEFAULT_PICKUP_STEP,
      deliveryID,
    }),
    fetchSuggestPOIs: pickupOrDropoff => ({
      type: duck.types.FETCH_SUGGEST_POIS,
      pickupOrDropoff,
    }),
    setSuggestPOIs: (pois, isPickup) => ({
      type: duck.types.SET_SUGGEST_POIS,
      pois,
      isPickup,
    }),
    fetchSavedPOIs: () => ({
      type: duck.types.FETCH_SAVED_POIS,
    }),
    setPredictPOIs: predictPOIs => ({
      type: duck.types.SET_PREDICT_POIS,
      predictPOIs,
    }),
    setFavoritePOIs: favoritePOIs => ({
      type: duck.types.SET_FAVORITE_POIS,
      favoritePOIs,
    }),
    // Saved POI R2 epic
    openAddressDetailOverlay: (poi, postSavingPoiCallback) => ({
      type: duck.types.OPEN_ADDRESS_DETAIL_OVERLAY,
      poi,
      postSavingPoiCallback,
    }),
    closeAddressDetailOverlay: () => ({
      type: duck.types.CLOSE_ADDRESS_DETAIL_OVERLAY,
    }),
    setCurrentEditingPOI: poi => ({
      type: duck.types.SET_CURRENT_EDITING_POI,
      poi,
    }),
    updateCurrentEditingPOIInfo: (fn, fv) => ({
      type: duck.types.UPDATE_CURRENT_EDITING_POI_INFO,
      fn,
      fv,
    }),
    saveCurrentPOI: () => ({
      type: duck.types.SAVE_FAVORITE_POI,
    }),
    deleteCurrentPOI: () => ({
      type: duck.types.DELETE_FAVORITE_POI,
    }),
  }),
  reducer: (state, action, duck) => {
    switch (action.type) {
      case duck.types.RECEIVE_USER_LOCATION: {
        const { latitude, longitude } = action;
        if (!latitude || !longitude) {
          return state;
        }
        return {
          ...state,
          userLocation: {
            latitude,
            longitude,
          },
        };
      }
      case duck.types.FETCH_SUGGEST_POIS: {
        const { pickupOrDropoff } = action;
        const oldSuggestPOIs = state.suggestPOIs;
        if (pickupOrDropoff === 'pickup') {
          return {
            ...state,
            suggestPOIs: {
              ...oldSuggestPOIs,
              pickup: {
                ...oldSuggestPOIs.pickup,
                fetching: true,
              },
            },
          };
        }
        return {
          ...state,
          suggestPOIs: {
            ...oldSuggestPOIs,
            dropoff: {
              ...oldSuggestPOIs.dropoff,
              fetching: true,
            },
          },
        };
      }
      case duck.types.SET_SUGGEST_POIS: {
        const { pois, isPickup } = action;
        const oldSuggestPOIs = state.suggestPOIs;
        if (isPickup) {
          return {
            ...state,
            suggestPOIs: {
              ...oldSuggestPOIs,
              pickup: {
                pois: pois,
                fetching: false,
              },
            },
          };
        }
        return {
          ...state,
          suggestPOIs: {
            ...oldSuggestPOIs,
            dropoff: {
              pois: pois,
              fetching: false,
            },
          },
        };
      }
      case duck.types.SET_PREDICT_POIS: {
        const { predictPOIs } = action;
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            predictPOIs,
          },
        };
      }
      case duck.types.SET_FAVORITE_POIS: {
        const { favoritePOIs } = action;
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            favoritePOIs,
          },
        };
      }
      case duck.types.OPEN_ADDRESS_DETAIL_OVERLAY: {
        const { poi, postSavingPoiCallback } = action;
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            addressDetailOverlay: {
              ...state.addressDetailOverlay,
              addressDetailOverlayVisible: true,
              overlayType: geoOverlayType.addressDetail,
            },
            currentEditingPOI: {
              originalPOI: _cloneDeep(poi),
              currentPOI: _cloneDeep(poi),
              postSavingPoiCallback,
            },
          },
        };
      }
      case duck.types.CLOSE_ADDRESS_DETAIL_OVERLAY: {
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            addressDetailOverlay: {
              ...state.addressDetailOverlay,
              addressDetailOverlayVisible: false,
              overlayType: undefined,
            },
            currentEditingPOI: undefined,
          },
        };
      }
      case duck.types.SET_CURRENT_EDITING_POI: {
        const { poi } = action;
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            currentEditingPOI: {
              ...state.savedPOI.currentEditingPOI,
              currentPOI: { ...poi },
            },
          },
        };
      }
      case duck.types.UPDATE_CURRENT_EDITING_POI_INFO: {
        const { fn, fv } = action;
        let oldPOI = { ...state.savedPOI.currentEditingPOI.currentPOI };
        if (fn === addressDetailFormField.savedPlaceLabel.fieldName) {
          _set(oldPOI, 'label', fv);
        } else {
          _set(oldPOI, `sp_ext_info.${fn}`, fv);
        }
        return {
          ...state,
          savedPOI: {
            ...state.savedPOI,
            currentEditingPOI: {
              ...state.savedPOI.currentEditingPOI,
              currentPOI: oldPOI,
            },
          },
        };
      }
      default: {
        return state;
      }
    }
  },
});
